import type { Icon as TablerIcon } from '@tabler/icons-react'
import {
  IconUserCheck, IconGitPullRequest, IconCircleCheck, IconMessageQuestion, IconSend,
  IconStack2, IconUsersGroup, IconUsers, IconHourglassLow,
} from '@tabler/icons-react'
import type { EntradaItem, InboxCounts, MovimentoDaEntrada, PastaDaEntrada } from '@/api/chamados'
import { rotuloDoStatus } from '@/components/chamados/StatusBadge'

/** As pastas na ordem da spec §4 (a mesma de `chamados.entrada.PASTAS`), com o nome escrito
 *  do ponto de vista de quem olha. "Todos os chamados" não é pasta: é o link para a lista. */
export const PASTAS: { chave: PastaDaEntrada; rotulo: string; Icon: TablerIcon }[] = [
  { chave: 'assigned', rotulo: 'Atribuídos a mim', Icon: IconUserCheck },
  { chave: 'gmud_action', rotulo: 'GMUD esperando você', Icon: IconGitPullRequest },
  { chave: 'aguardando_validacao', rotulo: 'Esperando sua validação', Icon: IconCircleCheck },
  { chave: 'waiting', rotulo: 'Esperando sua resposta', Icon: IconMessageQuestion },
  { chave: 'meus_chamados', rotulo: 'Meus chamados', Icon: IconSend },
  { chave: 'minhas_filas', rotulo: 'Triagem', Icon: IconStack2 },
  { chave: 'team_unassigned', rotulo: 'Do meu time, sem responsável', Icon: IconUsersGroup },
  { chave: 'team_assigned', rotulo: 'Do meu time, com responsável', Icon: IconUsers },
  { chave: 'parados', rotulo: 'Parados há mais de 30 dias úteis', Icon: IconHourglassLow },
]

const CHAVES = new Set<string>(PASTAS.map((p) => p.chave))

/** As pastas em que o responsável varia (spec 2026-10-22 §2). Nas outras ele é sempre quem
 *  olha (a estrela já diz) ou sempre vazio, e a coluna só tiraria largura do assunto. */
const PASTAS_COM_RESPONSAVEL = new Set<PastaDaEntrada>([
  'aguardando_validacao', 'waiting', 'meus_chamados', 'team_assigned', 'parados',
])

export const temResponsavel = (pasta: PastaDaEntrada | null) =>
  pasta != null && PASTAS_COM_RESPONSAVEL.has(pasta)

export function ehPasta(valor: string | null): valor is PastaDaEntrada {
  return valor != null && CHAVES.has(valor)
}

export function rotuloDaPasta(pasta: PastaDaEntrada): string {
  return PASTAS.find((p) => p.chave === pasta)?.rotulo ?? pasta
}

/** Spec §4: a primeira pasta, na ordem, com item em negrito; senão a primeira com item;
 *  senão Atribuídos a mim. */
export function pastaInicial(counts: InboxCounts | undefined): PastaDaEntrada {
  if (!counts) return 'assigned'
  const negrito = counts.negrito_por_pasta ?? {}
  const comNegrito = PASTAS.find((p) => (negrito[p.chave] ?? 0) > 0)
  if (comNegrito) return comNegrito.chave
  const comItem = PASTAS.find((p) => (counts[p.chave] ?? 0) > 0)
  return comItem?.chave ?? 'assigned'
}

/** Parados só aparece para quem tem chamado parado (o cliente nunca tem: §5.5). As outras
 *  pastas ficam visíveis vazias — a pasta que some é a que esconde o erro (§4). */
export function pastaVisivel(pasta: PastaDaEntrada, counts: InboxCounts | undefined, ativa: PastaDaEntrada): boolean {
  if (pasta !== 'parados') return true
  return pasta === ativa || (counts?.parados ?? 0) > 0
}

/** "21 dias úteis", "1 dia útil", "menos de 1 dia útil" (§5.4). */
export function textoDaEspera(dias: number): string {
  if (dias < 1) return 'menos de 1 dia útil'
  return dias === 1 ? '1 dia útil' : `${dias} dias úteis`
}

/** De quem é a vez, do ponto de vista de quem olha (§5.4). `vez` é quem deve o próximo
 *  movimento: 'atendimento' quando o solicitante falou por último. */
export function textoDaVez(vez: EntradaItem['vez'], souSolicitante: boolean): string {
  if (souSolicitante) return vez === 'solicitante' ? 'aguardando você' : 'com o atendimento'
  return vez === 'atendimento' ? 'esperando vocês' : 'sem retorno do cliente'
}

// As frases que o backend usa quando o movimento não tem texto próprio
// (`chamados.entrada._FRASES` e o trecho da atribuição): vêm depois do nome, sem dois-pontos.
const FRASES = new Set([
  'abriu o chamado', 'mudou o status', 'validou o atendimento', 'recusou a solução',
  'validou a execução', 'deu o aceite da execução', 'anexou um arquivo', 'iniciou a triagem',
  'concluiu a triagem',
  'encaminhou o chamado', 'tramitou o chamado', 'comentou', 'movimentou o chamado',
  'removeu o responsável', 'assumiu o chamado',
])

/** O trecho da linha (§5.3): "Bruno: o disco encheu", "Você iniciou a triagem", "Carlos mudou
 *  o status para Em atendimento". O texto já vem puro e cortado do servidor. */
export function textoDoMovimento(mov: MovimentoDaEntrada, souSolicitante: boolean): string {
  const quem = mov.autor_primeiro_nome
  if (mov.tipo === 'status_changed' && mov.para_status) {
    const para = rotuloDoStatus(mov.para_status, { visao: souSolicitante ? 'solicitante' : 'atendimento' })
    return `${quem} mudou o status para ${para}`
  }
  const trecho = mov.trecho.trim()
  if (!trecho) return quem
  if (FRASES.has(trecho) || trecho.startsWith('atribuiu a ')) return `${quem} ${trecho}`
  return `${quem}: ${trecho}`
}

/** "24/10" a partir de "2026-10-24", sem passar por `Date` (que mudaria o dia com o fuso). */
export function diaEMes(isoData: string): string {
  const [, mes, dia] = isoData.split('-')
  return dia && mes ? `${dia}/${mes}` : isoData
}
